import { useQuery } from '@tanstack/react-query';
import { get } from './api';
import { AttendanceDetails, AttendanceItem, CallHistoryItem, AttendanceSettings, ContactView, Message, Operator, SyncTask } from './types';

export type ListView = 'queue' | 'mine' | 'active' | 'finished' | 'helpable';

/** `days`/`q` valem para Finalizados (período e busca no servidor) e `q` também para Todos. */
export const useAttendances = (view: ListView, enabled = true, params: { days?: number; q?: string } = {}) => {
  const qs = new URLSearchParams({ view });
  if (view === 'finished') qs.set('days', String(params.days ?? 1));
  if ((view === 'finished' || view === 'active') && params.q?.trim()) qs.set('q', params.q.trim());
  return useQuery({
    queryKey: ['attendances', view, view === 'finished' || view === 'active' ? qs.toString() : ''],
    queryFn: () => get<AttendanceItem[]>(`/attendances?${qs}`),
    enabled,
  });
};

/** Atendimentos anteriores (e atual) de um contato, do mais recente para o mais antigo. */
export const useContactHistory = (contactId: string | null) =>
  useQuery({
    queryKey: ['attendances', 'contact', contactId],
    queryFn: () => get<AttendanceItem[]>(`/attendances?view=contact&contactId=${contactId}`),
    enabled: Boolean(contactId),
  });

/** Ligações com o contato (atendidas, perdidas e feitas), da mais recente para a mais antiga. */
export const useContactCalls = (contactId: string | null) =>
  useQuery({
    queryKey: ['calls', 'contact', contactId],
    queryFn: () => get<CallHistoryItem[]>(`/contacts/${contactId}/calls`),
    enabled: Boolean(contactId),
  });

export const useAttendance = (id: string | null) =>
  useQuery({ queryKey: ['attendance', id], queryFn: () => get<AttendanceDetails>(`/attendances/${id}`), enabled: Boolean(id) });

/** Só as mensagens deste atendimento: cada atendimento é uma conversa à parte. */
export const useMessages = (conversationId: string | null, attendanceId: string | null) =>
  useQuery({
    queryKey: ['messages', conversationId, attendanceId],
    // A API devolve da mais nova para a mais antiga; a tela mostra em ordem cronológica.
    queryFn: async () => (await get<Message[]>(`/conversations/${conversationId}/messages?limit=200&attendanceId=${attendanceId}`)).reverse(),
    enabled: Boolean(conversationId && attendanceId),
  });

export const useContact = (id: string | null) =>
  useQuery({ queryKey: ['contact', id], queryFn: () => get<ContactView>(`/contacts/${id}`), enabled: Boolean(id) });

/** Atalhos do cliente do contato (ex.: Uniplus Web); só quando o contato tem cliente. */
export const useCustomerShortcuts = (contactId: string | null, customerId: string | null) =>
  useQuery({
    queryKey: ['customer-shortcuts', contactId, customerId],
    queryFn: () => get<{ uniplusWeb: string | null }>(`/contacts/${contactId}/customer-shortcuts`),
    enabled: Boolean(contactId && customerId),
    staleTime: 10 * 60_000,
  });

export const useSyncTasks = (attendanceId: string | null) =>
  useQuery({ queryKey: ['sync', attendanceId], queryFn: () => get<SyncTask[]>(`/attendances/${attendanceId}/sync`), enabled: Boolean(attendanceId) });

export const useOperators = () => useQuery({ queryKey: ['operators'], queryFn: () => get<Operator[]>('/operators'), staleTime: 60_000 });

export const usePresence = () =>
  useQuery({
    queryKey: ['presence'],
    queryFn: async () => new Set((await get<{ online: Array<{ userId: string }> }>('/presence')).online.map((o) => o.userId)),
  });

export const useAttendanceSettings = () =>
  useQuery({ queryKey: ['settings', 'attendance'], queryFn: () => get<AttendanceSettings>('/settings/attendance'), staleTime: 5 * 60_000 });

/** Link do chamado no painel do Agidesk, para o atendente (`agidesk.agent_ticket_link_template`). */
export function useTicketLink(ticketId: string | null | undefined): string | null {
  const s = useAttendanceSettings().data;
  const template = s?.agentTicketLinkTemplate ?? s?.ticketLinkTemplate;
  return ticketId && template ? template.replaceAll('{id}', ticketId).replaceAll('{code}', ticketId) : null;
}

/** Código como o Agidesk exibe (FLEX-908); chamados antigos sem código mostram #número. */
export const ticketLabel = (a: { ticketId: string | null; ticketCode?: string | null }) => (a.ticketId ? (a.ticketCode ?? `#${a.ticketId}`) : null);
